import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  Users, Award, Activity, DollarSign, Trophy, Star, Filter, 
  FileText, TrendingUp, PieChart as PieChartIcon, Upload, Download,
  CheckCircle, XCircle, Loader2, X, Clock, Building2, Edit2, Trash2,
  Link, Copy, ExternalLink, Key, RefreshCw, Search, UserX, UserCheck
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeamView = ({ users, setShowInviteModal, refreshUsers, businessId }) => {
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  const [urlCopied, setUrlCopied] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    role: '',
    status: 'all' // all, active, inactive
  });
  
  // Edit member state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: '',
    department: '',
    job_title: ''
  });
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  
  // Volunteer hours edit state
  const [showVolunteerModal, setShowVolunteerModal] = useState(false);
  const [volunteerForm, setVolunteerForm] = useState({
    volunteer_hours_annual_cap: 0,
    volunteer_hours_balance: 0,
    volunteer_hours_used: 0
  });
  const [savingVolunteer, setSavingVolunteer] = useState(false);
  
  // Employee Portal URL
  const portalUrl = `${window.location.origin}/impact/${businessId}`;
  
  const copyPortalUrl = () => {
    navigator.clipboard.writeText(portalUrl);
    setUrlCopied(true);
    setTimeout(() => setUrlCopied(false), 2000);
  };

  // Search functionality
  const handleSearch = async () => {
    if (!searchQuery.trim() && !filters.department && filters.role === '' && filters.status === 'all') {
      setSearchResults(null);
      return;
    }
    
    setSearching(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery.trim()) params.append('q', searchQuery.trim());
      if (filters.department) params.append('department', filters.department);
      if (filters.role) params.append('role', filters.role);
      if (filters.status !== 'all') params.append('is_active', filters.status === 'active');
      
      const response = await axios.get(
        `${BACKEND_URL}/api/users/search?${params.toString()}`,
        { withCredentials: true }
      );
      setSearchResults(response.data.users);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery || filters.department || filters.role || filters.status !== 'all') {
        handleSearch();
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filters]);

  const clearSearch = () => {
    setSearchQuery('');
    setFilters({ department: '', role: '', status: 'all' });
    setSearchResults(null);
  };

  const handleResetPassword = async () => {
    if (!editingMember) return;
    setResettingPassword(true);
    setNewPassword('');
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/users/${editingMember.user_id}/reset-password`,
        {},
        { withCredentials: true }
      );
      setNewPassword(response.data.temp_password);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Toggle user active status (deactivate/activate)
  const handleToggleStatus = async (member) => {
    const newStatus = !(member.is_active !== false); // Default to active if not set
    const action = newStatus ? 'activate' : 'deactivate';
    
    if (!window.confirm(`Are you sure you want to ${action} ${member.name || member.email}?`)) {
      return;
    }
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/${member.user_id}/status`,
        { is_active: newStatus },
        { withCredentials: true }
      );
      if (refreshUsers) refreshUsers();
      if (searchResults) handleSearch();
    } catch (err) {
      alert(err.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  // Edit volunteer hours
  const handleEditVolunteerHours = (member) => {
    setEditingMember(member);
    setVolunteerForm({
      volunteer_hours_annual_cap: member.volunteer_hours_annual_cap || 0,
      volunteer_hours_balance: member.volunteer_hours_balance || 0,
      volunteer_hours_used: member.volunteer_hours_used || 0
    });
    setShowVolunteerModal(true);
  };

  const handleSaveVolunteerHours = async () => {
    if (!editingMember) return;
    setSavingVolunteer(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/${editingMember.user_id}/volunteer-hours`,
        volunteerForm,
        { withCredentials: true }
      );
      setShowVolunteerModal(false);
      setEditingMember(null);
      if (refreshUsers) refreshUsers();
      if (searchResults) handleSearch();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update volunteer hours');
    } finally {
      setSavingVolunteer(false);
    }
  };

  const handleEditClick = (member) => {
    setEditingMember(member);
    setEditForm({
      name: member.name || '',
      email: member.email || '',
      role: member.role || 'employee',
      department: member.department || '',
      job_title: member.job_title || ''
    });
    setNewPassword('');
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/users/${editingMember.user_id}`,
        editForm,
        { withCredentials: true }
      );
      setShowEditModal(false);
      setEditingMember(null);
      if (refreshUsers) refreshUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Are you sure you want to remove ${member.name || member.email} from the team?`)) {
      return;
    }
    try {
      await axios.delete(
        `${BACKEND_URL}/api/users/${member.user_id}`,
        { withCredentials: true }
      );
      if (refreshUsers) refreshUsers();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }

    setImporting(true);
    setImportResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/employees/bulk-import`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      setImportResults(response.data);
      if (refreshUsers) refreshUsers();
    } catch (err) {
      setImportResults({
        success: false,
        error: err.response?.data?.detail || 'Import failed'
      });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    window.open(`${BACKEND_URL}/api/employees/import-template`, '_blank');
  };

  const downloadCredentials = () => {
    if (!importResults?.imported_users?.length) return;
    
    const csv = [
      'email,name,role,department,temporary_password',
      ...importResults.imported_users.map(u => 
        `${u.email},${u.name},${u.role},${u.department || ''},${u.temp_password}`
      )
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `imported_employees_credentials_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Calculate stats from actual users
  const adminCount = users.filter(u => u.role === 'business_admin' || u.role === 'super_admin').length;
  const employeeCount = users.filter(u => u.role === 'employee').length;
  const viewOnlyCount = users.filter(u => u.role === 'view_only_admin').length;

  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Top Stats Row */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5" style={{ color: COLORS.primary }} />
            <TrendingUp className="w-3 h-3 text-green-500" />
          </div>
          <p className="text-xl font-bold" style={{ color: COLORS.primary }}>{users.length}</p>
          <p className="text-xs text-gray-500">Total Members</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Award className="w-5 h-5 text-purple-500" />
            <span className="text-xs text-gray-500">Admins</span>
          </div>
          <p className="text-xl font-bold text-purple-600">{adminCount}</p>
          <p className="text-xs text-gray-500">Business Admins</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-5 h-5 text-green-500" />
            <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">{employeeCount}</span>
          </div>
          <p className="text-xl font-bold text-green-600">{employeeCount}</p>
          <p className="text-xs text-gray-500">Employees</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5" style={{ color: COLORS.secondary }} />
            <TrendingUp className="w-3 h-3 text-green-500" />
          </div>
          <p className="text-xl font-bold" style={{ color: COLORS.secondary }}>$127</p>
          <p className="text-xs text-gray-500">Avg. Donation</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <Star className="w-3 h-3 text-amber-500" />
          </div>
          <p className="text-xl font-bold text-amber-600">{viewOnlyCount}</p>
          <p className="text-xs text-gray-500">View-Only</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left - Team List */}
        <div className="col-span-8 space-y-4">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold" style={{ color: COLORS.accent }}>Team Members</h2>
                <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {searchResults ? `${searchResults.length} found` : `${users.length} members`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowBulkImport(true)}
                  className="px-4 py-2 text-sm rounded-lg font-medium flex items-center gap-2 border hover:bg-gray-50"
                  style={{ borderColor: COLORS.primary, color: COLORS.primary }}
                >
                  <Upload className="w-4 h-4" /> Bulk Import
                </button>
                <button 
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 text-white text-sm rounded-lg font-medium flex items-center gap-2"
                  style={{ background: COLORS.primary }}
                >
                  <Users className="w-4 h-4" /> Invite User
                </button>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search by name, email, department..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(searchQuery || searchResults) && (
                  <button 
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 text-sm rounded-lg border flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-300 text-blue-600' : 'hover:bg-gray-50'}`}
              >
                <Filter className="w-4 h-4" /> Filters
              </button>
              {searching && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
            </div>
            
            {/* Filters */}
            {showFilters && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Department</label>
                  <input
                    type="text"
                    placeholder="Filter by department"
                    value={filters.department}
                    onChange={(e) => setFilters({...filters, department: e.target.value})}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm"
                  />
                </div>
                <div className="w-40">
                  <label className="text-xs text-gray-500 mb-1 block">Role</label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters({...filters, role: e.target.value})}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm"
                  >
                    <option value="">All Roles</option>
                    <option value="business_admin">Admin</option>
                    <option value="employee">Employee</option>
                    <option value="view_only_admin">View Only</option>
                  </select>
                </div>
                <div className="w-40">
                  <label className="text-xs text-gray-500 mb-1 block">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ background: COLORS.light }}>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Member</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Employee ID</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Department</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Vol. Hours</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(searchResults || users).map((member, i) => (
                  <tr key={member.user_id || i} className={`border-b hover:bg-gray-50 ${member.is_active === false ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: member.is_active === false ? '#9CA3AF' : COLORS.primary }}>
                          {(member.name || member.email)?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name || 'Unnamed'}</p>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 font-mono">{member.employee_id || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        member.role === 'business_admin' ? 'bg-blue-100 text-blue-700' :
                        member.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                        member.role === 'view_only_admin' ? 'bg-gray-100 text-gray-600' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {member.role?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{member.department || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button 
                        onClick={() => handleEditVolunteerHours(member)}
                        className="hover:bg-gray-100 rounded px-1 -ml-1"
                        title="Click to edit volunteer hours"
                      >
                        {member.volunteer_hours_annual_cap > 0 ? (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-600">
                              {member.volunteer_hours_balance || 0}/{member.volunteer_hours_annual_cap}h
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Set hours</span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        member.is_active === false 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {member.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button 
                          onClick={() => handleEditClick(member)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                          title="Edit member"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleToggleStatus(member)}
                          className={`p-1.5 rounded-lg transition-all ${
                            member.is_active === false 
                              ? 'text-gray-400 hover:text-green-600 hover:bg-green-50' 
                              : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'
                          }`}
                          title={member.is_active === false ? 'Activate user' : 'Deactivate user'}
                        >
                          {member.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </button>
                        <button 
                          onClick={() => handleDeleteMember(member)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Remove member"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {(searchResults || users).length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      {searchResults !== null ? 'No members match your search criteria.' : 'No team members yet. Invite users or bulk import from CSV.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right - Stats & Charts */}
        <div className="col-span-4 space-y-4">
          {/* Participation Chart */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Participation Rate</h3>
              <span className="text-xs text-gray-500">This Month</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <ProgressRing progress={users.length > 0 ? 85 : 0} size={80} strokeWidth={8} color={COLORS.success} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold" style={{ color: COLORS.success }}>{users.length > 0 ? '85%' : '0%'}</span>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Donating</span>
                  <span className="font-medium">{Math.round(users.length * 0.85)}/{users.length}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Not yet</span>
                  <span className="font-medium">{Math.round(users.length * 0.15)}/{users.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Role Distribution */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Role Distribution</h3>
              <PieChartIcon className="w-4 h-4 text-gray-400" />
            </div>
            <ResponsiveContainer width="100%" height={100}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Admins', value: adminCount || 1 },
                    { name: 'View Only', value: viewOnlyCount || 1 },
                    { name: 'Employees', value: employeeCount || 1 },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={25}
                  outerRadius={40}
                  paddingAngle={2}
                  dataKey="value"
                >
                  <Cell fill={COLORS.primary} />
                  <Cell fill="#8B5CF6" />
                  <Cell fill={COLORS.secondary} />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-3 gap-1 text-xs mt-2">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.primary }}></span>Admins {adminCount}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span>View {viewOnlyCount}</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.secondary }}></span>Staff {employeeCount}</span>
            </div>
          </div>

          {/* Employee Portal Link */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Link className="w-4 h-4" style={{ color: COLORS.primary }} />
              <h3 className="font-semibold text-sm" style={{ color: COLORS.primary }}>Employee Portal</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              Share this link with employees to access the Impact Portal, donate to campaigns, and log volunteer hours.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={portalUrl}
                readOnly
                className="flex-1 px-3 py-2 text-xs bg-white border rounded-lg text-gray-600 font-mono"
              />
              <button
                onClick={copyPortalUrl}
                className={`px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${
                  urlCopied 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-white border hover:bg-gray-50 text-gray-600'
                }`}
              >
                {urlCopied ? (
                  <>
                    <CheckCircle className="w-3 h-3" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy
                  </>
                )}
              </button>
              <a
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-lg text-xs font-medium bg-white border hover:bg-gray-50 text-gray-600 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Open
              </a>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold text-sm mb-3" style={{ color: COLORS.accent }}>Quick Actions</h3>
            <div className="space-y-2">
              <button 
                onClick={() => setShowInviteModal(true)}
                className="w-full p-3 rounded-lg text-left text-sm flex items-center gap-2 hover:shadow-sm border"
                style={{ background: `${COLORS.primary}10` }}
              >
                <Users className="w-4 h-4" style={{ color: COLORS.primary }} />
                <span style={{ color: COLORS.primary }}>Invite New Member</span>
              </button>
              <button 
                onClick={() => setShowBulkImport(true)}
                className="w-full p-3 rounded-lg text-left text-sm flex items-center gap-2 hover:shadow-sm border bg-green-50"
              >
                <Upload className="w-4 h-4 text-green-600" />
                <span className="text-green-700">Bulk Import from CSV</span>
              </button>
              <button className="w-full p-3 rounded-lg text-left text-sm flex items-center gap-2 hover:shadow-sm border bg-purple-50">
                <FileText className="w-4 h-4 text-purple-600" />
                <span className="text-purple-700">Export Team Report</span>
              </button>
            </div>
          </div>

          {/* HR Integration Preview */}
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold text-sm mb-3" style={{ color: COLORS.accent }}>HR Integration</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-600">Workday</span>
                </div>
                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full">Coming Soon</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-600">BambooHR</span>
                </div>
                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Bulk Import Employees</h3>
                <p className="text-sm text-gray-500 mt-1">Upload a CSV file to import multiple employees at once</p>
              </div>
              <button 
                onClick={() => { setShowBulkImport(false); setImportResults(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {!importResults ? (
                <>
                  {/* Instructions */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <h4 className="font-semibold text-blue-800 mb-2">CSV Format</h4>
                    <p className="text-sm text-blue-700 mb-3">
                      Your CSV file should have these columns (email is required, others are optional):
                    </p>
                    <code className="block text-xs bg-blue-100 p-3 rounded-lg text-blue-800 overflow-x-auto">
                      email, name, role, department, job_title, employee_id, manager_email, location, volunteer_hours_cap
                    </code>
                  </div>

                  {/* Download Template */}
                  <button
                    onClick={downloadTemplate}
                    className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-gray-400 transition-all mb-4 flex items-center justify-center gap-2 text-gray-600"
                  >
                    <Download className="w-5 h-5" />
                    <span>Download CSV Template</span>
                  </button>

                  {/* File Upload */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                    className="w-full p-6 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2"
                    style={{ background: importing ? '#9CA3AF' : COLORS.primary }}
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Upload CSV File
                      </>
                    )}
                  </button>

                  {/* Future HR Integration Note */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-500 text-center">
                      <strong>Coming Soon:</strong> Direct integration with Workday, BambooHR, and other HR systems for automatic employee sync.
                    </p>
                  </div>
                </>
              ) : (
                /* Import Results */
                <div>
                  {importResults.error ? (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                      <h4 className="font-semibold text-red-800 mb-2">Import Failed</h4>
                      <p className="text-sm text-red-600">{importResults.error}</p>
                    </div>
                  ) : (
                    <>
                      {/* Summary */}
                      <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                        <div className="flex items-center gap-3 mb-4">
                          <CheckCircle className="w-10 h-10 text-green-500" />
                          <div>
                            <h4 className="font-semibold text-green-800">Import Complete!</h4>
                            <p className="text-sm text-green-600">
                              {importResults.successful} of {importResults.total_processed} employees imported successfully
                            </p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-2xl font-bold text-gray-900">{importResults.total_processed}</p>
                            <p className="text-xs text-gray-500">Total Rows</p>
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-2xl font-bold text-green-600">{importResults.successful}</p>
                            <p className="text-xs text-gray-500">Imported</p>
                          </div>
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-2xl font-bold text-red-500">{importResults.failed}</p>
                            <p className="text-xs text-gray-500">Failed</p>
                          </div>
                        </div>
                      </div>

                      {/* Download Credentials */}
                      {importResults.imported_users?.length > 0 && (
                        <button
                          onClick={downloadCredentials}
                          className="w-full p-4 bg-blue-600 text-white rounded-xl font-semibold mb-4 flex items-center justify-center gap-2 hover:bg-blue-700"
                        >
                          <Download className="w-5 h-5" />
                          Download Credentials (with Temporary Passwords)
                        </button>
                      )}

                      {/* Errors */}
                      {importResults.errors?.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <h5 className="font-semibold text-red-800 mb-2">Errors ({importResults.errors.length})</h5>
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {importResults.errors.map((err, i) => (
                              <div key={i} className="text-xs text-red-600 flex gap-2">
                                <span className="text-red-400">Row {err.row}:</span>
                                <span>{err.email || 'No email'} - {err.error}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Close Button */}
                      <button
                        onClick={() => { setShowBulkImport(false); setImportResults(null); }}
                        className="w-full mt-4 p-3 border rounded-xl font-medium text-gray-600 hover:bg-gray-50"
                      >
                        Close
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && editingMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Edit Team Member</h3>
                <p className="text-sm text-gray-500 mt-1">Update member information</p>
              </div>
              <button 
                onClick={() => { setShowEditModal(false); setEditingMember(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                  placeholder="Enter email"
                  disabled
                />
                <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="employee">Employee</option>
                  <option value="view_only">View Only</option>
                  <option value="business_admin">Business Admin</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input
                  type="text"
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Engineering, Marketing"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  value={editForm.job_title}
                  onChange={(e) => setEditForm({ ...editForm, job_title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Software Engineer"
                />
              </div>
              
              {/* Password Reset Section */}
              <div className="border-t pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Password Reset</label>
                {newPassword ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium mb-1">New temporary password:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 px-3 py-2 bg-white rounded border font-mono text-sm">{newPassword}</code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(newPassword);
                          alert('Password copied!');
                        }}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-green-600 mt-2">Share this password securely with the employee.</p>
                  </div>
                ) : (
                  <button
                    onClick={handleResetPassword}
                    disabled={resettingPassword}
                    className="w-full p-3 border border-amber-200 bg-amber-50 rounded-lg text-sm text-amber-700 hover:bg-amber-100 flex items-center justify-center gap-2"
                  >
                    {resettingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      <>
                        <Key className="w-4 h-4" />
                        Reset Password
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowEditModal(false); setEditingMember(null); setNewPassword(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="px-4 py-2 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                style={{ background: COLORS.primary }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Volunteer Hours Modal */}
      {showVolunteerModal && editingMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Edit Volunteer Hours</h3>
                <p className="text-sm text-gray-500 mt-1">{editingMember.name || editingMember.email}</p>
              </div>
              <button 
                onClick={() => { setShowVolunteerModal(false); setEditingMember(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Annual PTO Cap (hours)</label>
                <input
                  type="number"
                  value={volunteerForm.volunteer_hours_annual_cap}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, volunteer_hours_annual_cap: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 40"
                  min="0"
                  step="0.5"
                />
                <p className="text-xs text-gray-400 mt-1">Maximum volunteer PTO hours per year</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Available Balance (hours)</label>
                <input
                  type="number"
                  value={volunteerForm.volunteer_hours_balance}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, volunteer_hours_balance: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 32"
                  min="0"
                  step="0.5"
                />
                <p className="text-xs text-gray-400 mt-1">Current available hours for the employee</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Used Hours</label>
                <input
                  type="number"
                  value={volunteerForm.volunteer_hours_used}
                  onChange={(e) => setVolunteerForm({ ...volunteerForm, volunteer_hours_used: parseFloat(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 8"
                  min="0"
                  step="0.5"
                />
                <p className="text-xs text-gray-400 mt-1">Hours already used this year</p>
              </div>
              
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Annual Cap:</span>
                    <span className="font-medium">{volunteerForm.volunteer_hours_annual_cap}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Used:</span>
                    <span className="font-medium text-amber-600">{volunteerForm.volunteer_hours_used}h</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="text-gray-500">Available:</span>
                    <span className="font-medium text-green-600">{volunteerForm.volunteer_hours_balance}h</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowVolunteerModal(false); setEditingMember(null); }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVolunteerHours}
                disabled={savingVolunteer}
                className="px-4 py-2 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
                style={{ background: COLORS.primary }}
              >
                {savingVolunteer ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {savingVolunteer ? 'Saving...' : 'Save Hours'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamView;
